import type { MouseEvent, KeyboardEvent } from 'react';
import {
    AlertCircle, AlertTriangle, CheckCircle, ArrowUp, ArrowDown, ChevronsUpDown,
} from 'lucide-react';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { ColumnResizeHandle } from '../ui/ColumnResizeHandle';
import type { ConsBusinessRow, ConsBusinessSortField, SortOrder, KpiMode } from '../../types/consolidatedBusiness';
import { CB_STATUS_META, toCbStatus } from './status';
import { fmtNum, fmtDate, ampel, AMPEL_LABEL, kpiUnit } from './format';

interface Props {
    items: ConsBusinessRow[];
    sortBy: ConsBusinessSortField;
    sortOrder: SortOrder;
    onSort: (field: ConsBusinessSortField) => void;
    onSelect: (row: ConsBusinessRow) => void;
    selectedKey: string | null;
    compact: boolean;
    rowKey: (row: ConsBusinessRow) => string;
    /** Decides the unit in the three KPI headers — EUR for net and gross profit, qty for quantity. */
    kpiMode: KpiMode;
    /** When the user can edit, the Status cell opens the status menu in place. */
    canEdit?: boolean;
    onStatusClick?: (row: ConsBusinessRow, e: MouseEvent) => void;
    /**
     * Right-click a row → transaction history. Optional: a host that does not pass it simply
     * keeps the browser's native context menu everywhere.
     */
    onRowContextMenu?: (row: ConsBusinessRow, e: MouseEvent) => void;
}

interface ColDef {
    key: string;
    label: string;
    align: 'left' | 'right' | 'center';
    sort?: ConsBusinessSortField;
    essential?: boolean;
    /** Default width in px. The user's own width, once dragged, is kept in localStorage. */
    width: number;
    /** Width while the drawer is open — kept separately, so the two layouts never fight. */
    compactWidth?: number;
    /** Gets the KPI unit appended to its label. */
    kpi?: boolean;
}

/**
 * The Customer (HubSpot link) and Deal columns are GONE (D6). The HubSpot link moved into the
 * drawer header, and the Deal cell only ever opened the drawer that a row click opens anyway —
 * two columns of chrome for one link and one no-op.
 *
 * The N× batch-variant badge is gone with the grain (migration 132).
 *
 * WIDTHS. Every column carries a default px width and the table is `table-fixed`, so a cell
 * never widens itself to fit its content — long customer and product names are clipped with an
 * ellipsis and carry the full text in `title`. Customer used to be the widest column on the
 * page purely because one customer somewhere has a long name; it is now sized for the common
 * case and draggable for the rest.
 *
 * A width the user drags is theirs and survives reloads and restarts — see WIDTH_KEY below.
 * These defaults only ever apply until the first drag.
 */
const COLUMNS: ColDef[] = [
    { key: 'customer', label: 'Customer', align: 'left', sort: 'customerName', essential: true, width: 440, compactWidth: 165 },
    { key: 'product', label: 'Product', align: 'left', sort: 'productName', essential: true, width: 300, compactWidth: 140 },
    { key: 'salesMgr', label: 'Sales Mgr', align: 'left', sort: 'salesManagerName', width: 150 },
    { key: 'lastInvoice', label: 'Last invoice', align: 'left', sort: 'lastInvoice', width: 110 },
    { key: 'ytd', label: 'YTD', align: 'right', sort: 'kpiYTD', essential: true, width: 110, compactWidth: 95, kpi: true },
    { key: 'm12', label: 'Last 12mts', align: 'right', sort: 'kpi12m', width: 125, kpi: true },
    { key: 'yearAvg', label: 'Avg p. year', align: 'right', sort: 'kpiYearAvg', width: 125, kpi: true },
    { key: 'avgCycle', label: 'Avg Cycle', align: 'center', sort: 'avgCycle', width: 100 },
    { key: 'daysSince', label: 'Days since', align: 'center', sort: 'daysSince', essential: true, width: 115, compactWidth: 90 },
    { key: 'status', label: 'Status', align: 'center', essential: true, width: 80, compactWidth: 60 },
];

/**
 * The drawer layout (design audit 2026-10-07, S2-04, Roman: "Split, kompakter"). With the drawer
 * open at 1280 px the table had 456 px for 1'295 px of columns — 35 % visible, Days since and
 * Status off-screen. The compact set is now the five columns that decide the next click
 * (Sales Mgr and Last invoice are in the drawer for the selected row) at widths that fit half a
 * 1280 px screen. A width dragged in compact mode is kept under its own key.
 */
const COMPACT_COLUMNS = COLUMNS.filter(c => c.essential).map(c => ({ ...c, width: c.compactWidth ?? c.width }));

/**
 * The resize machinery — localStorage persistence, the drag listeners and the guard that keeps
 * a finished drag from re-sorting the column — lives in `useResizableColumns`, shared with the
 * BAB report tables. The storage key is unchanged, so widths dragged before the extraction
 * still apply.
 */
const WIDTH_KEY = 'consbusiness-col-widths';
const COMPACT_WIDTH_KEY = 'consbusiness-col-widths-compact';

/**
 * The cycle light. Three shapes, not three colours alone, and a name for the screen reader.
 * **No icon when `daysSince` is unknown** (design audit S1-07): `ampel()` reads a missing value
 * as 0, which drew a green "in cycle" beside a dash.
 */
function AmpelIcon({ daysSince, avgCycle }: { daysSince: number | null; avgCycle: number | null }) {
    if (daysSince === null || daysSince === undefined) return null;
    const a = ampel(daysSince, avgCycle);
    const label = AMPEL_LABEL[a];
    const Icon = a === 'red' ? AlertCircle : a === 'amber' ? AlertTriangle : CheckCircle;
    const tone = a === 'red' ? 'text-red-600' : a === 'amber' ? 'text-amber-600' : 'text-green-600';
    return (
        <span role="img" aria-label={label} title={label} className="inline-flex align-middle">
            <Icon aria-hidden="true" className={`h-4 w-4 ${tone}`} />
        </span>
    );
}

export function ConsBusinessTable({
    items, sortBy, sortOrder, onSort, onSelect, selectedKey, compact, rowKey, kpiMode, canEdit, onStatusClick,
    onRowContextMenu,
}: Props) {
    const cols = compact ? COMPACT_COLUMNS : COLUMNS;
    const unit = kpiUnit(kpiMode);
    const labelOf = (col: ColDef) => (col.kpi ? `${col.label} (${unit})` : col.label);

    // Two hooks, one per layout, each with the FULL list of its own columns — a partial list
    // would make the hook drop the other columns' stored widths on the next save.
    const wide = useResizableColumns(COLUMNS, WIDTH_KEY);
    const narrow = useResizableColumns(COMPACT_COLUMNS, COMPACT_WIDTH_KEY);
    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } = compact ? narrow : wide;
    const totalWidth = totalWidthOf(cols.map(c => c.key));

    /** Enter or Space on a focused row opens it, as a click does (design audit S1-01). */
    const onRowKey = (row: ConsBusinessRow) => (e: KeyboardEvent<HTMLTableRowElement>) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(row); }
    };

    const headerClick = (col: ColDef) => {
        if (shouldSuppressClick()) return;
        if (col.sort) onSort(col.sort);
    };

    const justifyCls = (a: ColDef['align']) =>
        a === 'right' ? 'justify-end' : a === 'center' ? 'justify-center' : 'justify-start';
    const alignCls = (a: ColDef['align']) =>
        a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left';

    return (
        // Bounded, so the header has something to stick against (design audit S2-02): the box
        // was 2'100 px tall with no max-height and the column names scrolled away.
        <div className="max-h-[calc(100vh-14rem)] overflow-auto rounded-lg border border-gray-200">
            {/* `minWidth` rather than a fixed width: the table fills a wide container (the last,
                width-less col swallows the slack) and scrolls horizontally in a narrow one
                instead of squeezing the columns back out of the widths the user set. */}
            <table className="w-full table-fixed divide-y divide-gray-200 text-sm" style={{ minWidth: totalWidth }}>
                <colgroup>
                    {cols.map(col => <col key={col.key} style={{ width: widthOf(col.key) }} />)}
                    <col />
                </colgroup>
                <thead>
                    <tr>
                        {cols.map(col => {
                            const sorted = col.sort && sortBy === col.sort;
                            const label = labelOf(col);
                            return (
                            // `sticky` on the cells, not `relative`: sticky is already positioned,
                            // so the absolute resize strip still works, and `relative` would
                            // override it and let the header scroll away.
                            <th
                                key={col.key}
                                className={`sticky top-0 z-10 select-none bg-gray-50 px-2 py-2 text-xs font-semibold text-gray-600 ${alignCls(col.align)}`}
                                aria-sort={sorted ? (sortOrder === 'ASC' ? 'ascending' : 'descending') : col.sort ? 'none' : undefined}
                                title={col.sort ? `${label} — click to sort, drag the edge to resize` : label}
                            >
                                {col.sort ? (
                                    // A button, so sorting works from the keyboard (design audit S1-01).
                                    <button
                                        type="button"
                                        onClick={() => headerClick(col)}
                                        className={`flex w-full items-center gap-1 rounded hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${justifyCls(col.align)}`}
                                    >
                                        <span className="truncate">{label}</span>
                                        <span className="shrink-0" aria-hidden="true">
                                            {sorted
                                                ? (sortOrder === 'ASC' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
                                                : <ChevronsUpDown className="h-3 w-3 text-gray-400" />}
                                        </span>
                                    </button>
                                ) : (
                                    <span className={`flex items-center ${justifyCls(col.align)}`}>
                                        <span className="truncate">{label}</span>
                                    </span>
                                )}
                                <ColumnResizeHandle
                                    label={col.label}
                                    active={resizingKey === col.key}
                                    onStart={x => startResize(col.key, x)}
                                    onReset={() => resetWidth(col.key)}
                                />
                            </th>
                            );
                        })}
                        <th className="sticky top-0 z-10 bg-gray-50 px-2 py-2" />
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                    {items.length === 0 && (
                        <tr>
                            <td colSpan={cols.length + 1} className="px-4 py-8 text-center text-gray-500">
                                No combinations match the current filters.
                            </td>
                        </tr>
                    )}
                    {items.map(row => {
                        const key = rowKey(row);
                        const status = toCbStatus(row.Status);
                        const meta = CB_STATUS_META[status];
                        const StatusIcon = meta.icon;
                        // Lost rows are quieter, but still readable: gray-500 is 4.8:1 on white
                        // (gray-400 was 2.5:1 — design audit S1-08).
                        const grey = status === 'Lost' ? 'text-gray-500' : 'text-gray-900';
                        const selected = key === selectedKey;
                        // Every cell is clipped, so the full value has to stay reachable —
                        // hovering is the only way to read a name the column is too narrow for.
                        const cell = 'truncate px-2 py-2';
                        return (
                            // Left-click opens the drawer, right-click the transaction history.
                            // They cannot collide: a right-click does not fire `click`, and the
                            // status cell stopPropagation's on onClick only.
                            <tr
                                key={key}
                                tabIndex={0}
                                aria-current={selected ? 'true' : undefined}
                                onClick={() => onSelect(row)}
                                onKeyDown={onRowKey(row)}
                                onContextMenu={onRowContextMenu
                                    ? e => { e.preventDefault(); onRowContextMenu(row, e); }
                                    : undefined}
                                className={`cursor-pointer focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-500 ${selected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                            >
                                {cols.map(col => {
                                    switch (col.key) {
                                        case 'customer':
                                            // The CustomerID is shown because it is the group key
                                            // (ID grain, migration 123): several accounts can share
                                            // a name, and they are now separate rows.
                                            return (
                                                <td
                                                    key={col.key}
                                                    className={`${cell} ${grey}`}
                                                    title={[row.CustomerName, row.CustomerID].filter(Boolean).join(' · ')}
                                                >
                                                    <span>{row.CustomerName ?? '–'}</span>
                                                    {row.CustomerID && (
                                                        <span className="ml-1.5 text-[11px] text-gray-500 tabular-nums">
                                                            {row.CustomerID}
                                                        </span>
                                                    )}
                                                </td>
                                            );
                                        case 'product':
                                            return (
                                                <td key={col.key} className={`${cell} ${grey}`} title={row.ProductName ?? undefined}>
                                                    {row.ProductName ?? '–'}
                                                </td>
                                            );
                                        case 'salesMgr':
                                            return (
                                                <td key={col.key} className={`${cell} ${grey}`} title={row.SalesManagerName ?? undefined}>
                                                    {row.SalesManagerName ?? '–'}
                                                </td>
                                            );
                                        case 'lastInvoice':
                                            return <td key={col.key} className={`${cell} ${grey}`}>{fmtDate(row.LastInvoice)}</td>;
                                        case 'ytd':
                                            return <td key={col.key} className={`${cell} text-right tabular-nums ${grey}`}>{fmtNum(row.KpiYTD)}</td>;
                                        case 'm12':
                                            return <td key={col.key} className={`${cell} text-right tabular-nums ${grey}`}>{fmtNum(row.Kpi12m)}</td>;
                                        case 'yearAvg':
                                            return <td key={col.key} className={`${cell} text-right tabular-nums ${grey}`}>{fmtNum(row.KpiYearAvg)}</td>;
                                        case 'avgCycle':
                                            return <td key={col.key} className={`${cell} text-center tabular-nums ${grey}`}>{fmtNum(row.AvgCycle)}</td>;
                                        case 'daysSince':
                                            return (
                                                <td key={col.key} className={`${cell} text-center tabular-nums ${grey}`}>
                                                    <span className="mr-1">{fmtNum(row.DaysSince)}</span>
                                                    <AmpelIcon daysSince={row.DaysSince} avgCycle={row.AvgCycle} />
                                                </td>
                                            );
                                        case 'status': {
                                            const icon = <StatusIcon className={`inline h-4 w-4 ${meta.iconClass}`} aria-label={meta.label} />;
                                            // Editable for EVERY state, Lost included. Lost used
                                            // to be a dead end reachable only through the drawer,
                                            // which is how combinations stayed wrongly written off.
                                            const editable = canEdit && onStatusClick;
                                            return (
                                                <td key={col.key} className={`${cell} text-center`}>
                                                    {editable ? (
                                                        <button
                                                            type="button"
                                                            onClick={e => { e.stopPropagation(); onStatusClick!(row, e); }}
                                                            onKeyDown={e => e.stopPropagation()}
                                                            className="inline-flex h-8 w-8 items-center justify-center rounded hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                                            title={`${meta.label} — click to change`}
                                                            aria-label={`Status: ${meta.label}. Change status`}
                                                        >
                                                            {icon}
                                                        </button>
                                                    ) : icon}
                                                </td>
                                            );
                                        }
                                        default:
                                            return <td key={col.key} />;
                                    }
                                })}
                                <td />
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
